import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { Button } from '@commons/utils/styledLibraryComponents';
import { EXTERNAL_FEATURE_PATHS } from '@commons/utils/featurePaths';
import { GENERIC_MODAL_CANCEL_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import Stepper from '@commons/Stepper';

import { getClientInfo } from '@selectors/client';

import { externalClient as externalClientService } from '@services/externalClient';

import ClientCommentCard from '../components/ClientCommentCard';
import ClientCompanyCard from '../components/ClientCompanyCard';
import ClientInformationCard from '../components/ClientInformationCard';

import { getCloneCatalogModal } from '../utils/getCloneCatalogModal';
import { getManualProductModal } from '../utils/getManualProductModal';

import CatalogStep from './components/CatalogStep';

import theme from '@theme';

import {
  BackButton,
  Breadcrumb,
  Content,
  Footer,
  FooterButtons,
  FormContainer,
  Header,
} from './styledComponents';

const STEP_KEYS = {
  INFORMATIONS: 'informations',
  CATALOG: 'catalog',
};

const CATALOG_CHOICES = {
  ADD_LATER: 'addLater',
  ADD_MANUALLY: 'addManually',
  CLONE: 'clone',
};

// Catalog modes expected by POST /v2/b2b/external-clients/create (COR-1626).
const CATALOG_MODES = {
  EMPTY: 'empty',
  MANUAL: 'manual',
  CLONE: 'clone',
};

/**
 * Two-step external-client creation flow, rendered as a full-screen route
 * (/external/clients/create): step 1 captures the client information, step 2 the product catalog.
 * Both steps live in this component so the information fields are preserved when navigating back
 * from the catalog step. The final save creates the client with its catalog (empty / manual / clone)
 * then returns to the clients list; the manual and clone pickers are hosted in modals.
 */
const ExternalClientCreation = (props) => {
  const {
    history,
    stores,
    storeName,
    openConfirmationModal,
    isGenericModalOpen,
    hasCentralKitchens,
    openModal,
    refreshModal,
    displaySuccessMessage,
    displayErrorMessage,
    pageLoading,
    pageLoaded,
  } = props;

  const goToList = () => {
    history.push(EXTERNAL_FEATURE_PATHS.EXTERNAL_CLIENTS);
  };

  const [name, setName] = useState('');
  const [selectedStores, setSelectedStores] = useState([]);
  const [externalReference, setExternalReference] = useState('');
  const [contact, setContact] = useState('');
  const [telephone, setTelephone] = useState('');
  const [contactEmails, setContactEmails] = useState('');
  const [address, setAddress] = useState('');
  const [postCode, setPostCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [legalName, setLegalName] = useState('');
  const [siret, setSiret] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [comment, setComment] = useState('');

  const [currentStep, setCurrentStep] = useState(STEP_KEYS.INFORMATIONS);
  const [catalogChoice, setCatalogChoice] = useState(CATALOG_CHOICES.ADD_LATER);

  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneSourceClientId, setCloneSourceClientId] = useState(null);

  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  const [isSaving, setIsSaving] = useState(false);

  const associatedStoresLabel = i18next.t('EXTERNAL_CLIENTS.CREATION.ASSOCIATED_STORES', {
    storeName: getClientStoreNameTranslation(storeName, true),
  });

  const steps = [
    { key: STEP_KEYS.INFORMATIONS, label: i18next.t('GENERAL.INFORMATIONS') },
    { key: STEP_KEYS.CATALOG, label: i18next.t('EXTERNAL_CLIENTS.CREATION.STEP_CATALOG') },
  ];

  const catalogChoices = [
    {
      value: CATALOG_CHOICES.ADD_LATER,
      label: i18next.t('EXTERNAL_CLIENTS.CREATION.CATALOG_ADD_LATER'),
    },
    {
      value: CATALOG_CHOICES.ADD_MANUALLY,
      label: i18next.t('EXTERNAL_CLIENTS.CREATION.CATALOG_ADD_MANUALLY'),
    },
    {
      value: CATALOG_CHOICES.CLONE,
      label: i18next.t('EXTERNAL_CLIENTS.CREATION.CATALOG_CLONE'),
    },
  ];

  const isNextDisabled = !name.trim() || selectedStores.length === 0;

  const hasCatalogSelectionStep =
    catalogChoice === CATALOG_CHOICES.ADD_MANUALLY || catalogChoice === CATALOG_CHOICES.CLONE;

  const isFormDirty = () => {
    const filledTextValues = [
      name,
      externalReference,
      contact,
      telephone,
      contactEmails,
      address,
      postCode,
      city,
      country,
      legalName,
      siret,
      vatNumber,
      comment,
    ];

    if (selectedStores.length > 0) {
      return true;
    }

    return filledTextValues.some((value) => !!value);
  };

  const handleClose = () => {
    if (!isFormDirty()) {
      goToList();
      return;
    }

    openConfirmationModal(
      getConfirmationModal({
        title: i18next.t('EXTERNAL_CLIENTS.CREATION.CLOSE_CONFIRMATION_TITLE'),
        icon: '/images/inpulse/warning-white-small.svg',
        content: i18next.t('EXTERNAL_CLIENTS.CREATION.CLOSE_CONFIRMATION_CONTENT'),
        actions: [
          GENERIC_MODAL_CANCEL_BUTTON(),
          {
            key: 1,
            color: 'inpulse-default',
            label: i18next.t('GENERAL.LEAVE'),
            icon: '/images/inpulse/close-white-small.svg',
            handleClick: () => {
              goToList();
            },
          },
        ],
      }),
    );
  };

  const goToCatalogStep = () => {
    setCurrentStep(STEP_KEYS.CATALOG);
  };

  const goToInformationStep = () => {
    setCurrentStep(STEP_KEYS.INFORMATIONS);
  };

  const buildCatalogPayload = () => {
    if (catalogChoice === CATALOG_CHOICES.ADD_MANUALLY) {
      return { mode: CATALOG_MODES.MANUAL, productIds: selectedProductIds };
    }

    if (catalogChoice === CATALOG_CHOICES.CLONE) {
      return { mode: CATALOG_MODES.CLONE, sourceExternalClientId: cloneSourceClientId };
    }

    return { mode: CATALOG_MODES.EMPTY };
  };

  const buildCreatePayload = () => ({
    name: name.trim(),
    storeIds: selectedStores.map(({ id }) => id),
    externalReference: externalReference || null,
    comment: comment || null,
    company: {
      companyName: legalName || null,
      address: address || null,
      postCode: postCode || null,
      city: city || null,
      country: country || null,
      contact: contact || null,
      telephone: telephone || null,
      siret: siret || null,
      vatNumber: vatNumber || null,
      emails: contactEmails || null,
    },
    catalog: buildCatalogPayload(),
  });

  /**
   * Create the external client with its catalog behind a page loader, then leave the wizard back to
   * the clients list — with a success or error toaster depending on the outcome.
   */
  const handleSaveClient = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    pageLoading();

    try {
      await externalClientService.createExternalClient(buildCreatePayload());

      displaySuccessMessage(i18next.t('EXTERNAL_CLIENTS.CREATION.SAVE_SUCCESS'));
      history.push(EXTERNAL_FEATURE_PATHS.EXTERNAL_CLIENTS);
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.CREATION.SAVE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const resetCloneModal = () => {
    setCloneModalOpen(false);
    setCloneSourceClientId(null);
  };

  const handleConfirmClone = () => {
    setCloneModalOpen(false);
    handleSaveClient();
  };

  const resetManualModal = () => {
    setManualModalOpen(false);
    setSelectedProductIds([]);
  };

  const handleConfirmManual = () => {
    setManualModalOpen(false);
    handleSaveClient();
  };

  /**
   * Catalog-step primary action when a mode requiring an extra selection step is picked.
   * Clone opens the source-client picker modal; manual opens the product picker modal.
   */
  const handleCatalogNextStep = () => {
    if (catalogChoice === CATALOG_CHOICES.CLONE) {
      setCloneModalOpen(true);

      return;
    }

    setManualModalOpen(true);
  };

  useEffect(() => {
    if (!cloneModalOpen) {
      return;
    }

    const cloneModalConfig = getCloneCatalogModal({
      stores,
      selectedClientId: cloneSourceClientId,
      onSelectClient: setCloneSourceClientId,
      onCancel: resetCloneModal,
      onConfirm: handleConfirmClone,
    });

    if (isGenericModalOpen) {
      refreshModal(cloneModalConfig);

      return;
    }

    openModal(cloneModalConfig);
  }, [cloneModalOpen, cloneSourceClientId, stores]);

  useEffect(() => {
    if (!manualModalOpen) {
      return;
    }

    const manualModalConfig = getManualProductModal({
      stores: selectedStores,
      hasCentralKitchens,
      selectedProductIds,
      onSelectionChange: setSelectedProductIds,
      onCancel: resetManualModal,
      onConfirm: handleConfirmManual,
    });

    if (isGenericModalOpen) {
      refreshModal(manualModalConfig);

      return;
    }

    openModal(manualModalConfig);
  }, [manualModalOpen, selectedProductIds, selectedStores]);

  const renderInformationStep = () => (
    <>
      <ClientInformationCard
        associatedStoresLabel={associatedStoresLabel}
        contact={contact}
        contactEmails={contactEmails}
        externalReference={externalReference}
        name={name}
        selectedStores={selectedStores}
        stores={stores}
        telephone={telephone}
        onChangeContact={setContact}
        onChangeContactEmails={setContactEmails}
        onChangeExternalReference={setExternalReference}
        onChangeName={setName}
        onChangeStores={setSelectedStores}
        onChangeTelephone={setTelephone}
      />
      <ClientCompanyCard
        address={address}
        city={city}
        country={country}
        legalName={legalName}
        postCode={postCode}
        siret={siret}
        vatNumber={vatNumber}
        onChangeAddress={setAddress}
        onChangeCity={setCity}
        onChangeCountry={setCountry}
        onChangeLegalName={setLegalName}
        onChangePostCode={setPostCode}
        onChangeSiret={setSiret}
        onChangeVatNumber={setVatNumber}
      />
      <ClientCommentCard comment={comment} onChangeComment={setComment} />
    </>
  );

  const renderCatalogStep = () => (
    <CatalogStep
      choices={catalogChoices}
      selectedChoice={catalogChoice}
      subtitle={i18next.t('EXTERNAL_CLIENTS.CREATION.CATALOG_SUBTITLE')}
      title={i18next.t('EXTERNAL_CLIENTS.CREATION.CATALOG_TITLE')}
      onSelectChoice={setCatalogChoice}
    />
  );

  const renderContent = () => {
    if (currentStep === STEP_KEYS.CATALOG) {
      return renderCatalogStep();
    }

    return renderInformationStep();
  };

  const renderCatalogPrimaryButton = () => {
    if (hasCatalogSelectionStep) {
      return (
        <Button
          color={'inpulse-default'}
          handleClick={handleCatalogNextStep}
          icon={'/images/inpulse/arrow-right-white.svg'}
          label={i18next.t('GENERAL.NEXT')}
        />
      );
    }

    return (
      <Button
        color={'inpulse-default'}
        handleClick={handleSaveClient}
        icon={'/images/inpulse/save-white-small.svg'}
        isDisabled={isSaving}
        label={i18next.t('GENERAL.SAVE')}
      />
    );
  };

  const renderFooterButtons = () => {
    if (currentStep === STEP_KEYS.CATALOG) {
      return (
        <>
          <Button
            color={'inpulse-outline'}
            handleClick={goToInformationStep}
            icon={'/images/inpulse/arrow-left-ip-black.svg'}
            label={i18next.t('GENERAL.BACK')}
          />
          {renderCatalogPrimaryButton()}
        </>
      );
    }

    return (
      <>
        <Button
          color={'inpulse-outline'}
          handleClick={handleClose}
          icon={'/images/inpulse/close-black-small.svg'}
          label={i18next.t('GENERAL.CANCEL')}
        />
        <Button
          color={'inpulse-default'}
          handleClick={goToCatalogStep}
          icon={'/images/inpulse/arrow-right-white.svg'}
          isDisabled={isNextDisabled}
          label={i18next.t('GENERAL.NEXT')}
        />
      </>
    );
  };

  return (
    <FormContainer>
      <Header>
        <BackButton src={'/images/inpulse/arrow-left-ip-black.svg'} onClick={handleClose} />
        <Breadcrumb>
          {`${i18next.t('FEATURE.EXTERNAL.CLIENTS')} / ${i18next.t(
            'EXTERNAL_CLIENTS.CREATION.NEW_CLIENT',
          )}`}
        </Breadcrumb>
      </Header>

      <Content>{renderContent()}</Content>

      <Footer>
        <Stepper
          activeBackgroundColor={theme.colors.modalHeaders.green.background}
          activeStepKey={currentStep}
          steps={steps}
        />
        <FooterButtons>{renderFooterButtons()}</FooterButtons>
      </Footer>
    </FormContainer>
  );
};

const mapStateToProps = (state) => ({
  stores: state.baseReducer.activeStores,
  storeName: getClientInfo(state.baseReducer.user).storeName,
  hasCentralKitchens: getClientInfo(state.baseReducer.user).hasCentralKitchens,
  isGenericModalOpen: state.modalReducer.GenericModalBool,
});

const mapDispatchToProps = (dispatch) => ({
  openConfirmationModal: (params) => {
    dispatch(openGenericModal(params));
  },
  openModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  displaySuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  displayErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExternalClientCreation);
